<template>
  <v-card 
    class="metric-card" 
    :class="{ 'metric-card--loading': loading }"
    elevation="3"
    @click="$emit('click', metric)"
  >
    <v-card-text class="pa-4">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <v-skeleton-loader type="text" class="mb-2" />
        <v-skeleton-loader type="heading" class="mb-2" />
        <v-skeleton-loader type="text" />
      </div>

      <!-- Content -->
      <div v-else class="metric-content">
        <!-- Header -->
        <div class="metric-header">
          <div class="metric-info">
            <div class="metric-title">
              {{ metric.title }}
            </div>
            <div v-if="metric.description" class="metric-description">
              {{ metric.description }}
            </div>
          </div>
          
          <div class="metric-icon">
            <v-avatar 
              :color="metric.color" 
              size="40"
              variant="tonal"
            >
              <v-icon :color="metric.color">
                {{ metric.icon || getDefaultIcon() }}
              </v-icon>
            </v-avatar>
          </div>
        </div>

        <!-- Value -->
        <div class="metric-value">
          <span class="value-text">
            {{ formatValue(metric.value, metric.format) }}
          </span>
          
          <div v-if="showTrend && metric.previousValue !== undefined" class="metric-trend">
            <TrendIndicator :trend="metric.trend" :change="metric.changePercentage" />
          </div>
        </div>

        <!-- Change Information -->
        <div v-if="metric.previousValue !== undefined" class="metric-change">
          <div class="change-text">
            <span :class="getChangeClass()">
              {{ formatChange() }}
            </span>
            <span class="change-period">vs período anterior</span>
          </div>
        </div>

        <!-- Progress Bar (if applicable) -->
        <div v-if="showProgress" class="metric-progress mt-3">
          <v-progress-linear
            :model-value="progressValue"
            :color="metric.color"
            height="6"
            rounded
          />
          <div class="progress-labels mt-1">
            <span class="progress-current">{{ formatValue(metric.value, metric.format) }}</span>
            <span class="progress-target">{{ formatValue(targetValue, metric.format) }}</span>
          </div>
        </div>

        <!-- Sparkline Chart (if data available) -->
        <div v-if="sparklineData && sparklineData.length > 0" class="metric-sparkline mt-3">
          <svg
            ref="sparklineRef"
            :width="sparklineWidth"
            :height="sparklineHeight"
            class="sparkline-svg"
          >
            <path
              :d="sparklinePath"
              :stroke="metric.color"
              stroke-width="2"
              fill="none"
            />
            <defs>
              <linearGradient :id="`gradient-${metric.id}`" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" :style="`stop-color:${metric.color};stop-opacity:0.3`" />
                <stop offset="100%" :style="`stop-color:${metric.color};stop-opacity:0.1`" />
              </linearGradient>
            </defs>
            <path
              :d="`${sparklinePath} L ${sparklineWidth} ${sparklineHeight} L 0 ${sparklineHeight} Z`"
              :fill="`url(#gradient-${metric.id})`"
            />
          </svg>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import TrendIndicator from './TrendIndicator.vue'
import type { KPIMetric } from '@/types/analytics'

// 📝 Props & Emits
interface Props {
  metric: KPIMetric
  loading?: boolean
  showTrend?: boolean
  showProgress?: boolean
  targetValue?: number
  sparklineData?: number[]
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  showTrend: true,
  showProgress: false,
  targetValue: 0,
  sparklineData: () => []
})

const emit = defineEmits<{
  click: [metric: KPIMetric]
}>()

// 🔄 Reactive State
const sparklineRef = ref<SVGElement>()
const sparklineWidth = 120
const sparklineHeight = 30

// 📊 Computed Properties
const progressValue = computed(() => {
  if (!props.showProgress || !props.targetValue) return 0
  return Math.min((props.metric.value / props.targetValue) * 100, 100)
})

const sparklinePath = computed(() => {
  if (!props.sparklineData || props.sparklineData.length < 2) return ''
  
  const data = props.sparklineData
  const maxValue = Math.max(...data)
  const minValue = Math.min(...data)
  const range = maxValue - minValue || 1
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * sparklineWidth
    const y = sparklineHeight - ((value - minValue) / range) * sparklineHeight
    return `${x},${y}`
  })
  
  return `M ${points.join(' L ')}`
})

// 🔧 Helper Functions
const formatValue = (value: number, format: string): string => {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    
    case 'percentage':
      return `${value.toFixed(1)}%`
    
    case 'time':
      // Assume value is in minutes
      const hours = Math.floor(value / 60)
      const minutes = value % 60
      return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
    
    case 'number':
    default:
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`
      }
      return value.toLocaleString('es-DO')
  }
}

const formatChange = (): string => {
  if (props.metric.previousValue === undefined) return ''
  
  const change = props.metric.value - props.metric.previousValue
  const changePercentage = ((change / props.metric.previousValue) * 100)
  
  const sign = change >= 0 ? '+' : ''
  const formattedChange = formatValue(Math.abs(change), props.metric.format)
  
  return `${sign}${formattedChange} (${sign}${changePercentage.toFixed(1)}%)`
}

const getChangeClass = (): string => {
  if (props.metric.previousValue === undefined) return ''
  
  const change = props.metric.value - props.metric.previousValue
  if (change > 0) return 'change-positive'
  if (change < 0) return 'change-negative'
  return 'change-neutral'
}

const getDefaultIcon = (): string => {
  const iconMap: Record<string, string> = {
    currency: 'mdi-currency-usd',
    percentage: 'mdi-percent',
    time: 'mdi-clock-outline',
    number: 'mdi-counter'
  }
  
  return iconMap[props.metric.format] || 'mdi-chart-line'
}
</script>

<style scoped>
.metric-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px !important;
  border: 1px solid transparent;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  border-color: rgba(25, 118, 210, 0.2);
}

.metric-card--loading {
  cursor: default;
}

.metric-card--loading:hover {
  transform: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.loading-state {
  padding: 8px 0;
}

.metric-content {
  min-height: 120px;
  display: flex;
  flex-direction: column;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.metric-info {
  flex: 1;
}

.metric-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #424242;
  line-height: 1.3;
}

.metric-description {
  font-size: 0.75rem;
  color: #757575;
  margin-top: 4px;
  line-height: 1.2;
}

.metric-icon {
  margin-left: 12px;
}

.metric-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.value-text {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
}

.metric-trend {
  margin-left: 8px;
}

.metric-change {
  margin-bottom: 8px;
}

.change-text {
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 4px;
}

.change-positive {
  color: #4caf50;
  font-weight: 600;
}

.change-negative {
  color: #f44336;
  font-weight: 600;
}

.change-neutral {
  color: #757575;
  font-weight: 500;
}

.change-period {
  color: #9e9e9e;
  font-size: 0.75rem;
}

.metric-progress {
  margin-top: auto;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  color: #757575;
}

.metric-sparkline {
  margin-top: auto;
  display: flex;
  justify-content: center;
}

.sparkline-svg {
  border-radius: 4px;
}

@media (max-width: 600px) {
  .metric-header {
    margin-bottom: 12px;
  }
  
  .metric-title {
    font-size: 0.85rem;
  }
  
  .value-text {
    font-size: 1.5rem;
  }
  
  .metric-icon .v-avatar {
    width: 32px !important;
    height: 32px !important;
  }
}
</style>
